import React, { useRef, useState, useEffect } from 'react';
import { Highlight, HighlightColor } from '../types';

const HIGHLIGHT_COLORS: { color: HighlightColor; bg: string; label: string }[] = [
  { color: 'yellow', bg: 'bg-yellow-200', label: 'Yellow' },
  { color: 'green',  bg: 'bg-green-200',  label: 'Green'  },
  { color: 'blue',   bg: 'bg-blue-200',   label: 'Blue'   },
  { color: 'pink',   bg: 'bg-pink-200',   label: 'Pink'   },
  { color: 'orange', bg: 'bg-orange-200', label: 'Orange' },
];

export const HIGHLIGHT_BG: Record<HighlightColor, string> = {
  yellow: 'bg-yellow-200',
  green:  'bg-green-200',
  blue:   'bg-blue-200',
  pink:   'bg-pink-200',
  orange: 'bg-orange-200',
};

interface Props {
  text: string;
  highlights: Highlight[];
  onChange: (highlights: Highlight[]) => void;
  className?: string;
  editable?: boolean;
}

interface Segment {
  text: string;
  start: number;
  end: number;
  color: HighlightColor | null;
  highlightIndex: number | null;
}

function buildSegments(text: string, highlights: Highlight[]): Segment[] {
  if (!highlights.length) {
    return [{ text, start: 0, end: text.length, color: null, highlightIndex: null }];
  }

  // Sort highlights by start, resolve overlaps by keeping later ones
  const sorted = [...highlights].sort((a, b) => a.start - b.start);
  const segments: Segment[] = [];
  let cursor = 0;

  for (let i = 0; i < sorted.length; i++) {
    const h = sorted[i];
    const start = Math.max(h.start, cursor);
    const end = Math.min(h.end, text.length);
    if (start >= end) continue;

    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), start: cursor, end: start, color: null, highlightIndex: null });
    }
    segments.push({ text: text.slice(start, end), start, end, color: h.color, highlightIndex: i });
    cursor = end;
  }

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), start: cursor, end: text.length, color: null, highlightIndex: null });
  }

  return segments;
}

export const HighlightableText: React.FC<Props> = ({
  text,
  highlights,
  onChange,
  className = '',
  editable = true,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // popover: color picker shown after user taps Highlight button
  const [popover, setPopover] = useState<{ start: number; end: number } | null>(null);
  // mobileHighlightReady: true when there's a valid selection inside our container on mobile
  const [mobileHighlightReady, setMobileHighlightReady] = useState(false);
  const isMobile = 'ontouchstart' in window;

  // Track whether there's an active selection inside our container (mobile only)
  useEffect(() => {
    if (!editable || !isMobile) return;
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !containerRef.current) {
        setMobileHighlightReady(false);
        return;
      }
      const range = sel.getRangeAt(0);
      if (containerRef.current.contains(range.commonAncestorContainer)) {
        setMobileHighlightReady(true);
      } else {
        setMobileHighlightReady(false);
      }
    };
    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [editable, isMobile]);

  const getSelectionOffsets = (): { start: number; end: number } | null => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !containerRef.current) return null;
    const range = selection.getRangeAt(0);
    const containerText = containerRef.current.textContent || '';

    const getOffset = (node: Node, offset: number): number => {
      let pos = 0;
      const walker = document.createTreeWalker(containerRef.current!, NodeFilter.SHOW_TEXT);
      let current = walker.nextNode();
      while (current) {
        if (current === node) return pos + offset;
        pos += (current.textContent || '').length;
        current = walker.nextNode();
      }
      return pos + offset;
    };

    const start = getOffset(range.startContainer, range.startOffset);
    const end = getOffset(range.endContainer, range.endOffset);
    if (start >= end || end > containerText.length) return null;
    return { start, end };
  };

  // Desktop: show color picker immediately on mouseup
  const handleMouseUp = () => {
    if (isMobile) return;
    const offsets = getSelectionOffsets();
    if (!offsets) return;
    window.getSelection()?.removeAllRanges();
    setPopover(offsets);
  };

  // Mobile: user taps the "Highlight" button after finishing selection
  const handleMobileHighlightButton = () => {
    const offsets = getSelectionOffsets();
    if (!offsets) return;
    window.getSelection()?.removeAllRanges();
    setMobileHighlightReady(false);
    setPopover(offsets);
  };

  const applyHighlight = (color: HighlightColor) => {
    if (!popover) return;
    const { start, end } = popover;

    const filtered = highlights.filter(h => h.end <= start || h.start >= end);
    const trimmed = highlights
      .filter(h => !(h.end <= start || h.start >= end))
      .flatMap(h => {
        const parts: Highlight[] = [];
        if (h.start < start) parts.push({ ...h, end: start });
        if (h.end > end) parts.push({ ...h, start: end });
        return parts;
      });

    onChange([...filtered, ...trimmed, { start, end, color }]);
    setPopover(null);
  };

  const removeHighlight = (index: number) => {
    onChange(highlights.filter((_, i) => i !== index));
  };

  const segments = buildSegments(text, highlights);

  return (
    <div className="relative">
      <div
        ref={containerRef}
        onMouseUp={handleMouseUp}
        className={`select-text cursor-text ${className}`}
      >
        {segments.map((seg, i) =>
          seg.color ? (
            <mark
              key={i}
              className={`${HIGHLIGHT_BG[seg.color]} rounded px-0.5 cursor-pointer`}
              title={editable ? 'Click to remove highlight' : undefined}
              onClick={editable ? () => removeHighlight(seg.highlightIndex!) : undefined}
            >
              {seg.text}
            </mark>
          ) : (
            <span key={i}>{seg.text}</span>
          )
        )}
      </div>

      {/* Mobile: persistent button shown when text is selected inside container */}
      {editable && isMobile && mobileHighlightReady && !popover && (
        <button
          onPointerDown={(e) => { e.preventDefault(); handleMobileHighlightButton(); }}
          className="mt-2 flex items-center gap-1.5 px-3 py-1.5 bg-yellow-400 text-yellow-900 text-xs font-semibold rounded-full shadow-md"
        >
          <span>🖊</span> Highlight selection
        </button>
      )}

      {/* Color picker popover (desktop: floats above; mobile: inline below) */}
      {popover && editable && (
        <>
          {!isMobile && (
            <div className="fixed inset-0 z-10" onClick={() => setPopover(null)} />
          )}
          <div
            className={`z-20 flex items-center gap-1 bg-white border border-gray-200 rounded-lg shadow-lg px-2 py-1.5 ${isMobile ? 'mt-2' : 'absolute'}`}
            style={!isMobile ? { left: '50%', top: 0, transform: 'translate(-50%, -110%)' } : undefined}
          >
            <span className="text-xs text-gray-500 mr-1">Highlight:</span>
            {HIGHLIGHT_COLORS.map(({ color, bg, label }) => (
              <button
                key={color}
                title={label}
                onClick={() => applyHighlight(color)}
                className={`w-6 h-6 rounded-full ${bg} border border-gray-300 hover:scale-110 transition-transform`}
              />
            ))}
            <button
              onClick={() => setPopover(null)}
              className="ml-1 text-xs text-gray-400 hover:text-gray-600"
            >✕</button>
          </div>
        </>
      )}
    </div>
  );
};
